/**
 * Settings.js
 * User settings page with password change functionality
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Accordion, Table, Pagination, Modal } from 'react-bootstrap';
import moment from 'moment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { authService, settingsService, emailTemplateService, portalService } from '../../services/api';
import RoleManagement from './RoleManagement';
import LocationSettingsSection from './LocationSettingsSection';

export const getPaginationPages = (currentPage, totalPages) => {
  if (!totalPages || totalPages <= 1) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-end', totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      'ellipsis-start',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages
    ];
  }

  return [
    1,
    'ellipsis-start',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis-end',
    totalPages
  ];
};

const Settings = () => {
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // State per reminder settings
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    daysBefore: 10,
    daysAfter: 5,
    payPerUseEnabled: false,
    payPerUseRunningOutThreshold: 2,
    fromEmail: 'noreply@sportus.com',
    fromName: 'Sportus'
  });
  const [reminderLogs, setReminderLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingPayPerUseType, setTestingPayPerUseType] = useState(null);

  // State per email templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({ name: '', subject: '', body: '' });
  const [selectedInvitationTemplateId, setSelectedInvitationTemplateId] = useState('');
  const [invitationTemplateForm, setInvitationTemplateForm] = useState({ subject: '', body: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const templateQuillRef = useRef(null);

  // State per sezioni informative portale atleta
  const [portalSections, setPortalSections] = useState([]);
  const [savingSections, setSavingSections] = useState(false);
  const initialPortalSectionsRef = useRef([]);

  // State per allegati portale atleta
  const [portalAttachments, setPortalAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [attachmentFormData, setAttachmentFormData] = useState({
    title: '',
    description: '',
    isEnabled: true,
    file: null
  });
  const [savingAttachment, setSavingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const attachmentFileInputRef = useRef(null);

  // State per consensi dinamici portale atleta
  const [portalConsents, setPortalConsents] = useState([]);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [editingConsent, setEditingConsent] = useState(null);
  const [consentFormData, setConsentFormData] = useState({
    title: '',
    description: '',
    isMandatory: true,
    checkboxLabel: '',
    isEnabled: true
  });
  const [savingConsent, setSavingConsent] = useState(false);
  const [deletingConsentId, setDeletingConsentId] = useState(null);

  // State per impostazioni certificati medici
  const [medicalCertSettings, setMedicalCertSettings] = useState({
    exemptionAge: 6
  });
  const [savingMedicalCert, setSavingMedicalCert] = useState(false);

  const currentUser = authService.getCurrentUser();
  const currentUserRoles = currentUser.roles;
  const isCurrentUserAdmin = currentUserRoles.includes('admin');

  const loadReminderSettings = useCallback(async () => {
    try {
      const result = await settingsService.getSettings();
      if (result.success && result.data) {
        const settings = {
          enabled: result.data.find(s => s.key === 'SubscriptionReminders.Enabled')?.value === 'true' || false,
          daysBefore: parseInt(result.data.find(s => s.key === 'SubscriptionReminders.DaysBefore')?.value || '10'),
          daysAfter: parseInt(result.data.find(s => s.key === 'SubscriptionReminders.DaysAfter')?.value || '5'),
          payPerUseEnabled: result.data.find(s => s.key === 'PayPerUseReminders.Enabled')?.value === 'true' || false,
          payPerUseRunningOutThreshold: parseInt(result.data.find(s => s.key === 'PayPerUseReminders.RunningOutThreshold')?.value) || 2,
          fromEmail: result.data.find(s => s.key === 'Email.FromEmail')?.value || 'noreply@sportus.com',
          fromName: result.data.find(s => s.key === 'Email.FromName')?.value || 'Sportus'
        };
        setReminderSettings(settings);
      }
    } catch (error) {
      console.error('Error loading reminder settings:', error);
    }
  }, []);

  const loadPortalSections = useCallback(async () => {
    try {
      const result = await settingsService.getSettings();
      if (result.success && result.data) {
        const sectionsSetting = result.data.find(s => s.key === 'Portal.Sections');
        if (sectionsSetting && sectionsSetting.value) {
          try {
            const parsed = JSON.parse(sectionsSetting.value);
            if (Array.isArray(parsed)) {
              const parsedWithVersion = parsed.map(s => ({
                ...s,
                version: s.version || 1
              }));
              setPortalSections(parsedWithVersion);
              initialPortalSectionsRef.current = JSON.parse(JSON.stringify(parsedWithVersion));
              return;
            }
          } catch (e) {
            console.error('Error parsing Portal.Sections JSON:', e);
          }
        }
        setPortalSections([]);
        initialPortalSectionsRef.current = [];
      }
    } catch (error) {
      console.error('Error loading portal sections:', error);
    }
  }, []);

  const loadReminderLogs = useCallback(async (page = 1) => {
    setLogsLoading(true);
    try {
      const result = await settingsService.getReminderLogs(page, 10);
      if (result.success && result.data) {
        setReminderLogs(result.data.items || []);
        setLogsPagination({
          currentPage: result.data.currentPage || 1,
          totalPages: result.data.totalPages || 1,
          totalCount: result.data.totalCount || 0
        });
      }
    } catch (error) {
      console.error('Error loading reminder logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // Load email templates
  const loadEmailTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const result = await emailTemplateService.getEmailTemplates();
      if (result.success && result.data) {
        setEmailTemplates(result.data);

        const invitationTemplate = result.data.find(template => {
          const templateName = (template?.name || '').toLowerCase();
          return templateName.includes('invito') || templateName.includes('invitation');
        });

        if (invitationTemplate) {
          setSelectedInvitationTemplateId(invitationTemplate.id);
          setInvitationTemplateForm({
            subject: invitationTemplate.subject || '',
            body: invitationTemplate.body || ''
          });
        } else {
          setSelectedInvitationTemplateId('');
          setInvitationTemplateForm({ subject: '', body: '' });
        }
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Load portal attachments
  const loadPortalAttachments = useCallback(async () => {
    setAttachmentsLoading(true);
    try {
      const result = await portalService.getAttachments();
      if (result.success && Array.isArray(result.data)) {
        setPortalAttachments(result.data);
      } else {
        setPortalAttachments([]);
      }
    } catch (error) {
      console.error('Error loading portal attachments:', error);
    } finally {
      setAttachmentsLoading(false);
    }
  }, []);

  // Load portal dynamic consents
  const loadPortalConsents = useCallback(async () => {
    setConsentsLoading(true);
    try {
      const result = await portalService.getConsents();
      if (result.success && Array.isArray(result.data)) {
        setPortalConsents(result.data);
      } else {
        setPortalConsents([]);
      }
    } catch (error) {
      console.error('Error loading portal consents:', error);
    } finally {
      setConsentsLoading(false);
    }
  }, []);

  // Load medical certificate settings
  const loadMedicalCertSettings = useCallback(async () => {
    try {
      const result = await settingsService.getSettings();
      if (result.success && result.data) {
        const exemptionSetting = result.data.find(s => s.key === 'MedicalCertificate.ExemptionAge');
        const age = exemptionSetting && exemptionSetting.value !== undefined && !isNaN(parseInt(exemptionSetting.value, 10))
          ? parseInt(exemptionSetting.value, 10)
          : 6;
        setMedicalCertSettings({ exemptionAge: age });
      }
    } catch (error) {
      console.error('Error loading medical certificate settings:', error);
    }
  }, []);

  // Carica reminder settings, logs, portal sections, allegati, consensi e certificati medici al mount
  useEffect(() => {
    if (isCurrentUserAdmin) {
      loadReminderSettings();
      loadMedicalCertSettings();
      loadPortalSections();
      loadPortalAttachments();
      loadPortalConsents();
      loadReminderLogs(1);
      loadEmailTemplates();
    }
  }, [isCurrentUserAdmin, loadReminderSettings, loadMedicalCertSettings, loadPortalSections, loadPortalAttachments, loadPortalConsents, loadReminderLogs, loadEmailTemplates]);

  const handleReminderSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = ['daysBefore', 'daysAfter', 'payPerUseRunningOutThreshold'];
    setReminderSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (numericFields.includes(name) ? parseInt(value) : value)
    }));
  };

  const handleSaveReminderSettings = async () => {
    setSavingReminders(true);
    try {
      const updates = [
        { key: 'Email.FromEmail', value: reminderSettings.fromEmail },
        { key: 'Email.FromName', value: reminderSettings.fromName },
        { key: 'SubscriptionReminders.Enabled', value: reminderSettings.enabled.toString() },
        { key: 'SubscriptionReminders.DaysBefore', value: reminderSettings.daysBefore.toString() },
        { key: 'SubscriptionReminders.DaysAfter', value: reminderSettings.daysAfter.toString() },
        { key: 'PayPerUseReminders.Enabled', value: reminderSettings.payPerUseEnabled.toString() },
        { key: 'PayPerUseReminders.RunningOutThreshold', value: reminderSettings.payPerUseRunningOutThreshold.toString() }
      ];

      for (const update of updates) {
        const result = await settingsService.updateSetting(update.key, update.value);
        if (!result.success) {
          throw new Error(`Errore aggiornamento ${update.key}`);
        }
      }

      setAlert({
        show: true,
        type: 'success',
        message: 'Impostazioni reminder salvate con successo!'
      });
    } catch (error) {
      console.error('Error saving reminder settings:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore nel salvare le impostazioni reminder'
      });
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSaveMedicalCertSettings = async (e) => {
    if (e) e.preventDefault();
    setSavingMedicalCert(true);
    try {
      const parsedAge = parseInt(medicalCertSettings.exemptionAge, 10);
      const ageVal = isNaN(parsedAge) ? 6 : Math.max(0, parsedAge);
      const result = await settingsService.updateSetting(
        'MedicalCertificate.ExemptionAge',
        ageVal.toString(),
        'Età minima sotto la quale il certificato medico non è obbligatorio'
      );
      if (result.success) {
        setMedicalCertSettings({ exemptionAge: ageVal });
        setAlert({
          show: true,
          type: 'success',
          message: 'Impostazioni certificati medici salvate con successo!'
        });
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore nel salvare le impostazioni certificati medici'
        });
      }
    } catch (error) {
      console.error('Error saving medical certificate settings:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore nel salvare le impostazioni certificati medici'
      });
    } finally {
      setSavingMedicalCert(false);
    }
  };

  const handleSendTestReminder = async () => {
    setTestingEmail(true);
    try {
      const result = await settingsService.sendTestReminder();
      setAlert({
        show: true,
        type: result.success ? 'success' : 'danger',
        message: result.message || (result.success ? 'Email di test inviata con successo!' : 'Errore invio email test')
      });
      // Ricarica i log
      loadReminderLogs(1);
    } catch (error) {
      console.error('Error sending test reminder:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore invio email test'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSendTestPayPerUseReminder = async (reminderType) => {
    setTestingPayPerUseType(reminderType);
    try {
      const result = await settingsService.sendTestPayPerUseReminder(reminderType);
      setAlert({
        show: true,
        type: result.success ? 'success' : 'danger',
        message: result.message || (result.success
          ? 'Email di test pay-per-use inviata con successo!'
          : 'Errore invio email test pay-per-use')
      });
      loadReminderLogs(1);
    } catch (error) {
      console.error('Error sending pay-per-use test reminder:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore invio email test pay-per-use'
      });
    } finally {
      setTestingPayPerUseType(null);
    }
  };

  // Email Template CRUD handlers
  const handleOpenCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateFormData({ name: '', subject: '', body: '' });
    setShowTemplateModal(true);
  };

  const handleOpenEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateFormData({ name: template.name, subject: template.subject, body: template.body });
    setShowTemplateModal(true);
  };

  const handleCloseTemplateModal = () => {
    if (!savingTemplate) {
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateFormData({ name: '', subject: '', body: '' });
    }
  };

  const getTemplateVariablesHint = (templateName) => {
    switch (templateName) {
      case 'recupero-password':
        return (
          <>
            <strong>Variabili per Recupero Password:</strong> {'{link}'} (link di reimpostazione), {'{email}'}, {'{fromName}'} (nome associazione).
          </>
        );
      case 'modulo-iscrizione':
        return (
          <>
            <strong>Variabili per Modulo Iscrizione:</strong> {'{destinatario}'} (nome genitore/atleta), {'{nomeAtleta}'}, {'{fromName}'}.
          </>
        );
      case 'accesso-atleta':
        return (
          <>
            <strong>Variabili per Accesso Portale:</strong> {'{nome}'}, {'{cognome}'}, {'{link}'} (link di accesso), {'{fromName}'}.
          </>
        );
      case 'reminder-abbonamento':
        return (
          <>
            <strong>Variabili per Reminder Abbonamento:</strong> {'{nome}'}, {'{cognome}'}, {'{dataScadenza}'}, {'{nomeCorso}'}, {'{nomeGruppo}'}, {'{fromName}'}.
          </>
        );
      case 'reminder-ingressi-in-esaurimento':
      case 'reminder-ingressi-esauriti':
        return (
          <>
            <strong>Variabili per Reminder Ingressi:</strong> {'{nome}'}, {'{cognome}'}, {'{nomeCorso}'}, {'{ingressiRimanenti}'}, {'{ingressiTotali}'}, {'{ingressiUsati}'}, {'{fromName}'}.
          </>
        );
      default:
        return (
          <>
            <strong>Variabili disponibili:</strong> {'{nome}'}, {'{cognome}'}, {'{dataScadenza}'}, {'{nomeCorso}'}, {'{nomeGruppo}'}, {'{ingressiRimanenti}'}, {'{ingressiTotali}'}, {'{ingressiUsati}'}, {'{fromName}'}.
          </>
        );
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateFormData.name.trim() || !templateFormData.subject.trim()) {
      setAlert({ show: true, type: 'danger', message: 'Nome e oggetto del template sono obbligatori' });
      return;
    }
    setSavingTemplate(true);
    try {
      let result;
      if (editingTemplate) {
        const nameToSave = editingTemplate.isSystem ? editingTemplate.name : templateFormData.name.trim();
        result = await emailTemplateService.updateEmailTemplate(
          editingTemplate.id, nameToSave, templateFormData.subject.trim(), templateFormData.body
        );
      } else {
        result = await emailTemplateService.createEmailTemplate(
          templateFormData.name.trim(), templateFormData.subject.trim(), templateFormData.body
        );
      }
      if (result.success) {
        setAlert({ show: true, type: 'success', message: result.message });
        handleCloseTemplateModal();
        loadEmailTemplates();
      } else {
        setAlert({ show: true, type: 'danger', message: result.message });
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore nel salvataggio del template' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id, name, isSystem) => {
    if (isSystem) {
      setAlert({ show: true, type: 'warning', message: 'I template di sistema non possono essere eliminati.' });
      return;
    }
    if (!window.confirm(`Eliminare il template "${name}"?`)) return;
    setDeletingTemplateId(id);
    try {
      const result = await emailTemplateService.deleteEmailTemplate(id);
      if (result.success) {
        setAlert({ show: true, type: 'success', message: result.message });
        loadEmailTemplates();
      } else {
        setAlert({ show: true, type: 'danger', message: result.message });
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore nell\'eliminazione del template' });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleLoadInvitationTemplate = (templateId) => {
    if (!templateId) {
      setSelectedInvitationTemplateId('');
      setInvitationTemplateForm({ subject: '', body: '' });
      return;
    }

    const template = emailTemplates.find(t => t.id === templateId);
    if (!template) return;

    setSelectedInvitationTemplateId(template.id);
    setInvitationTemplateForm({
      subject: template.subject,
      body: template.body.replace(/<[^>]*>/g, '')
    });
  };

  const handleSaveInvitationTemplate = async () => {
    if (!invitationTemplateForm.subject.trim()) {
      setAlert({ show: true, type: 'danger', message: 'L’oggetto del template di invito è obbligatorio.' });
      return;
    }

    const templateName = 'Invito Registrazione';

    try {
      let result;
      if (selectedInvitationTemplateId) {
        result = await emailTemplateService.updateEmailTemplate(
          selectedInvitationTemplateId,
          templateName,
          invitationTemplateForm.subject,
          invitationTemplateForm.body
        );
      } else {
        result = await emailTemplateService.createEmailTemplate(
          templateName,
          invitationTemplateForm.subject,
          invitationTemplateForm.body
        );
      }

      if (result.success) {
        setAlert({ show: true, type: 'success', message: result.message || 'Template invito salvato con successo.' });
        await loadEmailTemplates();
      } else {
        setAlert({ show: true, type: 'danger', message: result.message || 'Errore nel salvataggio del template.' });
      }
    } catch (error) {
      console.error('Error saving invitation template:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore nel salvataggio del template di invito.' });
    }
  };

  // Portal Custom Sections Handlers
  const handleAddPortalSection = () => {
    const newSection = {
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: '',
      content: '',
      enabled: true,
      order: portalSections.length + 1,
      version: 1
    };
    setPortalSections(prev => [...prev, newSection]);
  };

  const handleUpdatePortalSection = (id, field, value) => {
    setPortalSections(prev =>
      prev.map(section => (section.id === id ? { ...section, [field]: value } : section))
    );
  };

  const handleDeletePortalSection = (id, title) => {
    if (window.confirm(`Sei sicuro di voler eliminare la sezione "${title || 'Senza titolo'}"?`)) {
      setPortalSections(prev => {
        const filtered = prev.filter(s => s.id !== id);
        return filtered.map((s, idx) => ({ ...s, order: idx + 1 }));
      });
    }
  };

  const handleMovePortalSection = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= portalSections.length) return;
    setPortalSections(prev => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[targetIndex];
      updated[targetIndex] = temp;
      return updated.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  };

  const handleSavePortalSections = async () => {
    const emptyTitle = portalSections.find(s => !s.title || !s.title.trim());
    if (emptyTitle) {
      setAlert({
        show: true,
        type: 'danger',
        message: 'Tutte le sezioni devono avere un titolo compilato.'
      });
      return;
    }

    setSavingSections(true);
    try {
      // Calcola l'incremento di versione se il titolo o il contenuto sono cambiati rispetto allo snapshot iniziale
      const sectionsToSave = portalSections.map(section => {
        const initial = initialPortalSectionsRef.current.find(s => s.id === section.id);
        const currentVersion = section.version || 1;
        if (!initial) {
          return { ...section, version: currentVersion };
        }
        const hasContentChanged = initial.title?.trim() !== section.title?.trim() || initial.content !== section.content;
        return {
          ...section,
          version: hasContentChanged ? currentVersion + 1 : currentVersion
        };
      });

      const jsonValue = JSON.stringify(sectionsToSave);
      const result = await settingsService.updateSetting(
        'Portal.Sections',
        jsonValue,
        'Sezioni informative personalizzate per il portale atleta'
      );
      if (result.success) {
        setPortalSections(sectionsToSave);
        initialPortalSectionsRef.current = JSON.parse(JSON.stringify(sectionsToSave));
        setAlert({
          show: true,
          type: 'success',
          message: 'Sezioni informative del portale atleta salvate con successo!'
        });
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore durante il salvataggio delle sezioni'
        });
      }
    } catch (error) {
      console.error('Error saving portal sections:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante il salvataggio delle sezioni'
      });
    } finally {
      setSavingSections(false);
    }
  };

  // Portal Attachments Handlers
  const handleOpenCreateAttachment = () => {
    setEditingAttachment(null);
    setAttachmentFormData({
      title: '',
      description: '',
      isEnabled: true,
      file: null
    });
    if (attachmentFileInputRef.current) {
      attachmentFileInputRef.current.value = '';
    }
    setShowAttachmentModal(true);
  };

  const handleOpenEditAttachment = (attachment) => {
    setEditingAttachment(attachment);
    setAttachmentFormData({
      title: attachment.title,
      description: attachment.description || '',
      isEnabled: Boolean(attachment.isEnabled),
      file: null
    });
    if (attachmentFileInputRef.current) {
      attachmentFileInputRef.current.value = '';
    }
    setShowAttachmentModal(true);
  };

  const handleCloseAttachmentModal = () => {
    if (!savingAttachment) {
      setShowAttachmentModal(false);
      setEditingAttachment(null);
      setAttachmentFormData({ title: '', description: '', isEnabled: true, file: null });
      if (attachmentFileInputRef.current) {
        attachmentFileInputRef.current.value = '';
      }
    }
  };

  const handleSaveAttachment = async () => {
    if (!attachmentFormData.title.trim()) {
      setAlert({ show: true, type: 'danger', message: 'Il titolo dell\'allegato è obbligatorio.' });
      return;
    }

    if (!editingAttachment && !attachmentFormData.file) {
      setAlert({ show: true, type: 'danger', message: 'È necessario selezionare un file da caricare.' });
      return;
    }

    if (attachmentFormData.file) {
      const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'webp'];
      const ext = attachmentFormData.file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        setAlert({
          show: true,
          type: 'danger',
          message: 'Tipo di file non supportato. Sono ammessi file PDF, Word (.doc, .docx) e Immagini (.jpg, .jpeg, .png, .webp).'
        });
        return;
      }
      if (attachmentFormData.file.size > 25 * 1024 * 1024) {
        setAlert({
          show: true,
          type: 'danger',
          message: 'Il file selezionato supera la dimensione massima consentita di 25MB.'
        });
        return;
      }
    }

    setSavingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('title', attachmentFormData.title.trim());
      formData.append('description', attachmentFormData.description?.trim() || '');
      formData.append('isEnabled', attachmentFormData.isEnabled);

      if (attachmentFormData.file) {
        formData.append('file', attachmentFormData.file);
      }

      let result;
      if (editingAttachment) {
        result = await portalService.updateAttachment(editingAttachment.id, formData);
      } else {
        result = await portalService.createAttachment(formData);
      }

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: result.message || 'Allegato salvato con successo!'
        });
        handleCloseAttachmentModal();
        await loadPortalAttachments();
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore nel salvataggio dell\'allegato'
        });
      }
    } catch (error) {
      console.error('Error saving portal attachment:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante il salvataggio dell\'allegato'
      });
    } finally {
      setSavingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (id, title) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'allegato "${title}"? I consensi registrati per questo file verranno rimossi.`)) {
      return;
    }

    setDeletingAttachmentId(id);
    try {
      const result = await portalService.deleteAttachment(id);
      if (result.success) {
        setAlert({ show: true, type: 'success', message: result.message || 'Allegato eliminato con successo' });
        await loadPortalAttachments();
      } else {
        setAlert({ show: true, type: 'danger', message: result.message || 'Errore nell\'eliminazione dell\'allegato' });
      }
    } catch (error) {
      console.error('Error deleting portal attachment:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore durante l\'eliminazione dell\'allegato' });
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleToggleAttachmentActive = async (attachment, checked) => {
    try {
      const formData = new FormData();
      formData.append('isEnabled', checked);
      const result = await portalService.updateAttachment(attachment.id, formData);
      if (result.success) {
        setPortalAttachments(prev =>
          prev.map(a => (a.id === attachment.id ? { ...a, isEnabled: checked } : a))
        );
      } else {
        setAlert({ show: true, type: 'danger', message: result.message || 'Errore durante l\'aggiornamento dello stato' });
      }
    } catch (error) {
      console.error('Error toggling attachment active:', error);
    }
  };

  const handleMoveAttachment = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= portalAttachments.length) return;

    const updated = [...portalAttachments];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    setPortalAttachments(updated);

    try {
      const currentItem = updated[index];
      const targetItem = updated[targetIndex];

      const formData1 = new FormData();
      formData1.append('order', index + 1);
      await portalService.updateAttachment(currentItem.id, formData1);

      const formData2 = new FormData();
      formData2.append('order', targetIndex + 1);
      await portalService.updateAttachment(targetItem.id, formData2);
    } catch (error) {
      console.error('Error saving attachment order:', error);
    }
  };

  const handleDownloadAttachmentFile = async (attachment) => {
    setDownloadingAttachmentId(attachment.id);
    try {
      const res = await portalService.downloadAttachment(attachment);
      if (!res.success) {
        setAlert({ show: true, type: 'danger', message: res.message || 'Errore nel download del file' });
      }
    } catch (error) {
      console.error('Error downloading attachment file:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore nel download del file' });
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const getAttachmentFileIcon = (fileName) => {
    if (!fileName) return 'fas fa-file text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'fas fa-file-pdf text-danger';
    if (ext === 'doc' || ext === 'docx') return 'fas fa-file-word text-primary';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'fas fa-file-image text-success';
    return 'fas fa-file text-secondary';
  };

  const formatAttachmentSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      const kb = bytes / 1024;
      return `${kb.toFixed(1)} KB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Portal Consents Handlers
  const handleOpenCreateConsent = () => {
    setEditingConsent(null);
    setConsentFormData({
      title: '',
      description: '',
      isMandatory: true,
      checkboxLabel: 'Accetto',
      isEnabled: true
    });
    setShowConsentModal(true);
  };

  const handleOpenEditConsent = (consent) => {
    setEditingConsent(consent);
    setConsentFormData({
      title: consent.title || '',
      description: consent.description || '',
      isMandatory: Boolean(consent.isMandatory),
      checkboxLabel: consent.checkboxLabel || 'Accetto',
      isEnabled: Boolean(consent.isEnabled)
    });
    setShowConsentModal(true);
  };

  const handleCloseConsentModal = () => {
    if (!savingConsent) {
      setShowConsentModal(false);
      setEditingConsent(null);
      setConsentFormData({
        title: '',
        description: '',
        isMandatory: true,
        checkboxLabel: '',
        isEnabled: true
      });
    }
  };

  const handleSaveConsent = async () => {
    if (!consentFormData.title.trim()) {
      setAlert({ show: true, type: 'danger', message: 'Il titolo del consenso è obbligatorio.' });
      return;
    }
    if (!consentFormData.description.trim()) {
      setAlert({ show: true, type: 'danger', message: 'Il testo descrittivo del consenso è obbligatorio.' });
      return;
    }

    setSavingConsent(true);
    try {
      const payload = {
        title: consentFormData.title.trim(),
        description: consentFormData.description.trim(),
        isMandatory: consentFormData.isMandatory,
        checkboxLabel: consentFormData.checkboxLabel?.trim() || null,
        isEnabled: consentFormData.isEnabled,
        order: editingConsent ? editingConsent.order : portalConsents.length + 1
      };

      let result;
      if (editingConsent) {
        result = await portalService.updateConsent(editingConsent.id, payload);
      } else {
        result = await portalService.createConsent(payload);
      }

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: result.message || 'Consenso salvato con successo!'
        });
        handleCloseConsentModal();
        await loadPortalConsents();
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore durante il salvataggio del consenso'
        });
      }
    } catch (error) {
      console.error('Error saving portal consent:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante il salvataggio del consenso'
      });
    } finally {
      setSavingConsent(false);
    }
  };

  const handleDeleteConsent = async (id, title) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il consenso "${title}"? I consensi registrati per questa voce verranno rimossi.`)) {
      return;
    }

    setDeletingConsentId(id);
    try {
      const result = await portalService.deleteConsent(id);
      if (result.success) {
        setAlert({ show: true, type: 'success', message: result.message || 'Consenso eliminato con successo' });
        await loadPortalConsents();
      } else {
        setAlert({ show: true, type: 'danger', message: result.message || 'Errore nell\'eliminazione del consenso' });
      }
    } catch (error) {
      console.error('Error deleting portal consent:', error);
      setAlert({ show: true, type: 'danger', message: 'Errore durante l\'eliminazione del consenso' });
    } finally {
      setDeletingConsentId(null);
    }
  };

  const handleToggleConsentActive = async (consent, checked) => {
    try {
      const result = await portalService.updateConsent(consent.id, { isEnabled: checked });
      if (result.success) {
        setPortalConsents(prev =>
          prev.map(c => (c.id === consent.id ? { ...c, isEnabled: checked } : c))
        );
      } else {
        setAlert({ show: true, type: 'danger', message: result.message || 'Errore durante l\'aggiornamento dello stato' });
      }
    } catch (error) {
      console.error('Error toggling consent active:', error);
    }
  };

  const handleMoveConsent = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= portalConsents.length) return;

    const updated = [...portalConsents];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    setPortalConsents(updated);

    try {
      const currentItem = updated[index];
      const targetItem = updated[targetIndex];

      await portalService.updateConsent(currentItem.id, { order: index + 1 });
      await portalService.updateConsent(targetItem.id, { order: targetIndex + 1 });
    } catch (error) {
      console.error('Error saving consent order:', error);
    }
  };

  // Quill editor modules for template modal
  const templateQuillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  }), []);

  const templateQuillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background', 'align', 'link'
  ];

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-cog me-2"></i>
              Impostazioni
            </h2>
          </div>

          {alert.show && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, type: '', message: '' })}>
              {alert.message}
            </Alert>
          )}

          {!isCurrentUserAdmin ? (
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Questa sezione è riservata agli amministratori.
            </Alert>
          ) : (
            <Accordion className="mb-4">
              {/* Reminder Abbonamenti */}
              <Accordion.Item eventKey="reminders">
                    <Accordion.Header>
                      <i className="fas fa-envelope me-2"></i>
                      Reminder Abbonamenti (Scadenza e Consumo)
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form>
                        {/* Email Configuration Section */}
                        <div className="mb-4 p-3 border rounded bg-light">
                          <h6 className="mb-3">
                            <i className="fas fa-at me-2"></i>
                            Configurazione Email Mittente
                          </h6>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Email Mittente *</Form.Label>
                                <Form.Control
                                  type="email"
                                  name="fromEmail"
                                  value={reminderSettings.fromEmail}
                                  onChange={handleReminderSettingChange}
                                  placeholder="noreply@sportus.com"
                                />
                                <Form.Text className="text-muted">
                                  Indirizzo email da cui verranno inviati i reminder
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Nome Mittente *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="fromName"
                                  value={reminderSettings.fromName}
                                  onChange={handleReminderSettingChange}
                                  placeholder="Sportus"
                                />
                                <Form.Text className="text-muted">
                                  Nome che appare come mittente negli email
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>

                        {/* Reminder Settings Section */}
                        <div className="mb-4 p-3 border rounded bg-light">
                          <h6 className="mb-3">
                            <i className="fas fa-bell me-2"></i>
                            Impostazioni Reminder Scadenza
                          </h6>

                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="remindersEnabled"
                              name="enabled"
                              label="Abilita reminder scadenza abbonamenti"
                              checked={reminderSettings.enabled}
                              onChange={handleReminderSettingChange}
                            />
                          </Form.Group>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Giorni Prima Scadenza *</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="daysBefore"
                                  value={reminderSettings.daysBefore}
                                  onChange={handleReminderSettingChange}
                                  min="1"
                                  max="90"
                                  disabled={!reminderSettings.enabled}
                                />
                                <Form.Text className="text-muted">
                                  Invia reminder X giorni prima della scadenza
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Giorni Dopo Scadenza *</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="daysAfter"
                                  value={reminderSettings.daysAfter}
                                  onChange={handleReminderSettingChange}
                                  min="1"
                                  max="90"
                                  disabled={!reminderSettings.enabled}
                                />
                                <Form.Text className="text-muted">
                                  Invia reminder X giorni dopo la scadenza
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Alert variant="info" className="mb-0 py-2 small">
                            <i className="fas fa-info-circle me-2"></i>
                            Il testo e l&apos;oggetto dell&apos;email di promemoria vengono gestiti dal template di sistema <strong>reminder-abbonamento</strong> nella sezione <strong>Email Pre-impostate</strong>.
                          </Alert>
                        </div>

                        <div className="mb-4 p-3 border rounded bg-light">
                          <h6 className="mb-3">
                            <i className="fas fa-ticket-alt me-2"></i>
                            Impostazioni Reminder Abbonamenti a Consumo
                          </h6>

                          <Form.Group className="mb-3">
                            <Form.Check
                              type="switch"
                              id="payPerUseRemindersEnabled"
                              name="payPerUseEnabled"
                              label="Abilita reminder per ingressi in esaurimento/esauriti"
                              checked={reminderSettings.payPerUseEnabled}
                              onChange={handleReminderSettingChange}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Soglia numero ingressi in esaurimento *</Form.Label>
                            <Form.Control
                              type="number"
                              min="1"
                              name="payPerUseRunningOutThreshold"
                              value={reminderSettings.payPerUseRunningOutThreshold}
                              onChange={handleReminderSettingChange}
                              disabled={!reminderSettings.payPerUseEnabled}
                            />
                            <Form.Text className="text-muted">
                              Notifica e UI di "in esaurimento" scatteranno quando l'utente raggiunge questo numero o meno di ingressi (es: 2).
                            </Form.Text>
                          </Form.Group>

                          <Alert variant="info" className="mb-0 py-2 small">
                            <i className="fas fa-info-circle me-2"></i>
                            I testi e gli oggetti delle email per ingressi in esaurimento o esauriti vengono gestiti dai template di sistema <strong>reminder-ingressi-in-esaurimento</strong> e <strong>reminder-ingressi-esauriti</strong> nella sezione <strong>Email Pre-impostate</strong>.
                          </Alert>
                        </div>

                        <div className="d-flex gap-2">
                          <Button 
                            variant="primary" 
                            onClick={handleSaveReminderSettings}
                            disabled={savingReminders}
                          >
                            {savingReminders ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2"></i>
                                Salva Impostazioni
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="info" 
                            onClick={handleSendTestReminder}
                            disabled={testingEmail || !reminderSettings.enabled}
                          >
                            {testingEmail ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Inviando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-envelope me-2"></i>
                                Invia Email Test
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-warning"
                            onClick={() => handleSendTestPayPerUseReminder('running_out')}
                            disabled={testingPayPerUseType !== null || !reminderSettings.payPerUseEnabled}
                          >
                            {testingPayPerUseType === 'running_out' ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Inviando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-battery-quarter me-2"></i>
                                Test In Esaurimento
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleSendTestPayPerUseReminder('sold_out')}
                            disabled={testingPayPerUseType !== null || !reminderSettings.payPerUseEnabled}
                          >
                            {testingPayPerUseType === 'sold_out' ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Inviando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-ban me-2"></i>
                                Test Esaurito
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Certificati Medici */}
                  <Accordion.Item eventKey="medical-certificates">
                    <Accordion.Header>
                      <i className="fas fa-file-medical me-2"></i>
                      Certificati Medici
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form onSubmit={handleSaveMedicalCertSettings}>
                        <div className="mb-4 p-3 border rounded bg-light">
                          <h6 className="mb-3">
                            <i className="fas fa-child me-2"></i>
                            Esenzione Certificato per Età
                          </h6>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3" controlId="medicalCertExemptionAge">
                                <Form.Label>Età minima per obbligo certificato (anni)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  max="18"
                                  step="1"
                                  value={medicalCertSettings.exemptionAge}
                                  onChange={(e) => setMedicalCertSettings({ exemptionAge: e.target.value })}
                                  placeholder="6"
                                />
                                <Form.Text className="text-muted">
                                  Gli atleti al di sotto di questa età non sono considerati obbligati al certificato medico e non vengono conteggiati nei totali mancanti, scaduti o in scadenza della dashboard né nei rispettivi elenchi. Impostare a 0 per disabilitare l'esenzione e rendere il certificato obbligatorio per tutti.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>

                        <div className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={savingMedicalCert}
                          >
                            {savingMedicalCert ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Salvataggio...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2"></i>
                                Salva Impostazioni Certificati
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Email Pre-impostate */}
                  <Accordion.Item eventKey="templates">
                    <Accordion.Header>
                      <i className="fas fa-file-alt me-2"></i>
                      Email Pre-impostate
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-muted mb-0">
                          Crea template email riutilizzabili per l'invio massivo e i reminder.
                        </p>
                        <Button variant="primary" size="sm" onClick={handleOpenCreateTemplate}>
                          <i className="fas fa-plus me-2"></i>
                          Nuovo Template
                        </Button>
                      </div>                        

                      {templatesLoading ? (
                        <div className="text-center py-3">
                          <span className="spinner-border" />
                        </div>
                      ) : emailTemplates.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead>
                              <tr>
                                <th>Nome</th>
                                <th>Oggetto</th>
                                <th>Ultima Modifica</th>
                                <th style={{width: '120px'}}>Azioni</th>
                              </tr>
                            </thead>
                            <tbody>
                              {emailTemplates.map(template => (
                                <tr key={template.id}>
                                  <td>
                                    <strong>{template.name}</strong>
                                    {template.isSystem && (
                                      <span className="badge bg-primary ms-2">Sistema</span>
                                    )}
                                  </td>
                                  <td>{template.subject}</td>
                                  <td>{moment(template.updatedAt).format('DD/MM/YYYY HH:mm')}</td>
                                  <td>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-1"
                                      onClick={() => handleOpenEditTemplate(template)}
                                      title="Modifica template"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteTemplate(template.id, template.name, template.isSystem)}
                                      disabled={Boolean(template.isSystem) || deletingTemplateId === template.id}
                                      title={template.isSystem ? "I template di sistema non possono essere eliminati" : "Elimina template"}
                                    >
                                      {deletingTemplateId === template.id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        <i className="fas fa-trash-alt"></i>
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-file-alt fa-2x mb-2 d-block"></i>
                          <p>Nessun template email creato</p>
                        </div>
                      )}

                      <div className="mb-4 p-3 border rounded bg-light">
                        <h6 className="mb-3">
                          <i className="fas fa-user-plus me-2"></i>
                          Template Invito Registrazione
                        </h6>

                        {emailTemplates.length > 0 && (
                          <Form.Group className="mb-3">
                            <Form.Label>Carica da template pre-impostato</Form.Label>
                            <Form.Select
                              value={selectedInvitationTemplateId}
                              onChange={(e) => handleLoadInvitationTemplate(e.target.value)}
                            >
                              <option value="">— Nessuno —</option>
                              {emailTemplates.map(template => (
                                <option key={template.id} value={template.id}>{template.name}</option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Seleziona un template già creato e usa lo stesso flusso di configurazione per l’invito.
                            </Form.Text>
                          </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                          <Form.Label>Oggetto Email</Form.Label>
                          <Form.Control
                            type="text"
                            value={invitationTemplateForm.subject}
                            onChange={(e) => setInvitationTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                            placeholder="Oggetto dell'email di invito"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Corpo Email</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={6}
                            value={invitationTemplateForm.body}
                            onChange={(e) => setInvitationTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                            placeholder="Usa {link} come placeholder per inserire il link di invito."
                          />
                          <Form.Text className="text-muted">
                            Variabile disponibile: {'{link}'} per il link completo di registrazione.
                          </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                          <Button variant="primary" onClick={handleSaveInvitationTemplate}>
                            <i className="fas fa-save me-2"></i>
                            Salva template invito
                          </Button>
                        </div>
                      </div>
                      
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Sezioni Informative Portale Atleta */}
                  <Accordion.Item eventKey="portal-sections">
                    <Accordion.Header>
                      <i className="fas fa-info-circle me-2"></i>
                      Sezioni Informative Portale Atleta
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-muted mb-0 small">
                          Configura le sezioni informative personalizzate mostrate nel profilo atleta (es. Istruzioni Pagamenti, Responsabile Safeguarding). Le sezioni attive richiedono all&apos;atleta la presa visione per sbloccare il salvataggio del profilo.
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddPortalSection}
                          className="text-nowrap ms-3"
                        >
                          <i className="fas fa-plus me-1"></i>
                          Aggiungi Sezione
                        </Button>
                      </div>

                      {portalSections.length === 0 ? (
                        <Alert variant="light" className="border text-center py-4 text-muted">
                          <i className="fas fa-layer-group fa-2x mb-2 d-block text-secondary"></i>
                          Nessuna sezione informativa configurata. Clicca su &quot;Aggiungi Sezione&quot; per crearne una.
                        </Alert>
                      ) : (
                        <div className="d-flex flex-column gap-3 mb-4">
                          {portalSections.map((section, index) => (
                            <Card key={section.id} className="border shadow-sm">
                              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
                                <div className="d-flex align-items-center gap-2 flex-grow-1 me-2">
                                  <span className="badge bg-secondary">#{index + 1}</span>
                                  <strong className="text-truncate">
                                    {section.title || <span className="text-muted fst-italic">Nuova Sezione</span>}
                                  </strong>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Check
                                    type="switch"
                                    id={`portal-section-switch-${section.id}`}
                                    label={section.enabled ? 'Attiva' : 'Disattivata'}
                                    checked={Boolean(section.enabled)}
                                    onChange={(e) => handleUpdatePortalSection(section.id, 'enabled', e.target.checked)}
                                    className="me-2 mb-0 small fw-bold"
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === 0}
                                    onClick={() => handleMovePortalSection(index, -1)}
                                    title="Sposta su"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-up"></i>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === portalSections.length - 1}
                                    onClick={() => handleMovePortalSection(index, 1)}
                                    title="Sposta giù"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-down"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeletePortalSection(section.id, section.title)}
                                    title="Elimina sezione"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </div>
                              </Card.Header>
                              <Card.Body className="p-3">
                                <Form.Group className="mb-3">
                                  <Form.Label className="fw-semibold small">Titolo Sezione *</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Es: Informazioni Pagamenti, Responsabile Safeguarding..."
                                    value={section.title}
                                    onChange={(e) => handleUpdatePortalSection(section.id, 'title', e.target.value)}
                                  />
                                </Form.Group>

                                <Form.Group className="mb-0">
                                  <Form.Label className="fw-semibold small">Contenuto Informativo (HTML / Rich Text)</Form.Label>
                                  <ReactQuill
                                    theme="snow"
                                    value={section.content || ''}
                                    onChange={(val) => handleUpdatePortalSection(section.id, 'content', val)}
                                    modules={templateQuillModules}
                                    formats={templateQuillFormats}
                                    placeholder="Inserisci il testo informativo, elenchi, recapiti..."
                                    style={{ height: '180px', marginBottom: '45px' }}
                                  />
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          ))}
                        </div>
                      )}

                      {portalSections.length > 0 && (
                        <div className="d-flex justify-content-end">
                          <Button
                            variant="success"
                            onClick={handleSavePortalSections}
                            disabled={savingSections}
                          >
                            {savingSections ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Salvataggio in corso...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2"></i>
                                Salva Sezioni Portale
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Allegati Portale Atleta */}
                  <Accordion.Item eventKey="portal-attachments">
                    <Accordion.Header>
                      <i className="fas fa-paperclip me-2"></i>
                      Allegati Portale Atleta
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-muted mb-0 small">
                          Carica documenti e file (PDF, Word, immagini) da rendere disponibili per il download e l&apos;accettazione obbligatoria nel profilo atleta.
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleOpenCreateAttachment}
                          className="text-nowrap ms-3"
                        >
                          <i className="fas fa-plus me-1"></i>
                          Nuovo Allegato
                        </Button>
                      </div>

                      {attachmentsLoading ? (
                        <div className="text-center py-4">
                          <span className="spinner-border text-primary" />
                          <p className="text-muted mt-2 small">Caricamento allegati...</p>
                        </div>
                      ) : portalAttachments.length === 0 ? (
                        <Alert variant="light" className="border text-center py-4 text-muted">
                          <i className="fas fa-file-upload fa-2x mb-2 d-block text-secondary"></i>
                          Nessun allegato caricato per il portale atleta. Clicca su &quot;Nuovo Allegato&quot; per caricarne uno.
                        </Alert>
                      ) : (
                        <div className="d-flex flex-column gap-3 mb-3">
                          {portalAttachments.map((attachment, index) => (
                            <Card key={attachment.id} className="border shadow-sm">
                              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
                                <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 text-truncate">
                                  <span className="badge bg-secondary">#{index + 1}</span>
                                  <i className={`${getAttachmentFileIcon(attachment.fileName)} fa-lg`}></i>
                                  <strong className="text-truncate">{attachment.title}</strong>
                                  <small className="text-muted text-nowrap">
                                    {attachment.fileName} {formatAttachmentSize(attachment.fileSize)}
                                  </small>
                                  {attachment.fileVersion > 1 && (
                                    <span className="badge bg-light text-dark border">
                                      v{attachment.fileVersion}
                                    </span>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Check
                                    type="switch"
                                    id={`portal-attachment-switch-${attachment.id}`}
                                    label={attachment.isEnabled ? 'Attivo' : 'Disattivato'}
                                    checked={Boolean(attachment.isEnabled)}
                                    onChange={(e) => handleToggleAttachmentActive(attachment, e.target.checked)}
                                    className="me-2 mb-0 small fw-bold"
                                  />
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleDownloadAttachmentFile(attachment)}
                                    disabled={downloadingAttachmentId === attachment.id}
                                    title="Scarica / Visualizza file"
                                    className="px-2 py-1"
                                  >
                                    {downloadingAttachmentId === attachment.id ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                      <i className="fas fa-download"></i>
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === 0}
                                    onClick={() => handleMoveAttachment(index, -1)}
                                    title="Sposta su"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-up"></i>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === portalAttachments.length - 1}
                                    onClick={() => handleMoveAttachment(index, 1)}
                                    title="Sposta giù"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-down"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleOpenEditAttachment(attachment)}
                                    title="Modifica allegato"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteAttachment(attachment.id, attachment.title)}
                                    disabled={deletingAttachmentId === attachment.id}
                                    title="Elimina allegato"
                                    className="px-2 py-1"
                                  >
                                    {deletingAttachmentId === attachment.id ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                      <i className="fas fa-trash"></i>
                                    )}
                                  </Button>
                                </div>
                              </Card.Header>
                              {attachment.description && (
                                <Card.Body className="py-2 px-3 bg-white">
                                  <small className="text-muted">{attachment.description}</small>
                                </Card.Body>
                              )}
                            </Card>
                          ))}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Consensi Portale Atleta */}
                  <Accordion.Item eventKey="portal-consents">
                    <Accordion.Header>
                      <i className="fas fa-file-contract me-2"></i>
                      Consensi Portale Atleta
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <p className="text-muted mb-0 small flex-grow-1">
                          Configura i testi e i consensi legali richiesti all&apos;atleta (Privacy GDPR, Regolamento, Liberatoria Foto/Video, ecc.).
                          Se modifichi il titolo o il testo di un consenso esistente, la versione si incrementerà automaticamente e gli atleti dovranno rinnovare l&apos;accettazione.
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleOpenCreateConsent}
                          className="text-nowrap ms-auto"
                        >
                          <i className="fas fa-plus me-1"></i>
                          Aggiungi Consenso
                        </Button>
                      </div>

                      {consentsLoading ? (
                        <div className="text-center py-4">
                          <span className="spinner-border text-primary" />
                        </div>
                      ) : portalConsents.length === 0 ? (
                        <Alert variant="light" className="border text-center py-4 text-muted">
                          <i className="fas fa-file-contract fa-2x mb-2 d-block text-secondary"></i>
                          Nessun consenso configurato. Clicca su &quot;Aggiungi Consenso&quot; per crearne uno.
                        </Alert>
                      ) : (
                        <div className="d-flex flex-column gap-3 mb-4">
                          {portalConsents.map((consent, index) => (
                            <Card key={consent.id} className="border shadow-sm">
                              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                                  <span className="badge bg-secondary">#{index + 1}</span>
                                  <strong className="text-truncate">{consent.title}</strong>
                                  <span className={`badge ${consent.isMandatory ? 'bg-danger' : 'bg-info text-dark'}`}>
                                    {consent.isMandatory ? 'Obbligatorio' : 'Facoltativo'}
                                  </span>
                                  {consent.isSystem && (
                                    <span className="badge bg-dark">Sistema</span>
                                  )}
                                  <span className="badge bg-light text-dark border">v{consent.version || 1}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Check
                                    type="switch"
                                    id={`portal-consent-switch-${consent.id}`}
                                    label={consent.isEnabled ? 'Attivo' : 'Disattivato'}
                                    checked={Boolean(consent.isEnabled)}
                                    onChange={(e) => handleToggleConsentActive(consent, e.target.checked)}
                                    className="me-2 mb-0 small fw-bold"
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === 0}
                                    onClick={() => handleMoveConsent(index, -1)}
                                    title="Sposta su"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-up"></i>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={index === portalConsents.length - 1}
                                    onClick={() => handleMoveConsent(index, 1)}
                                    title="Sposta giù"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-arrow-down"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleOpenEditConsent(consent)}
                                    title="Modifica consenso"
                                    className="px-2 py-1"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Button>
                                  {consent.isSystem ? (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      disabled
                                      title="I consensi di sistema non possono essere eliminati"
                                      className="px-2 py-1"
                                    >
                                      <i className="fas fa-lock"></i>
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteConsent(consent.id, consent.title)}
                                      disabled={deletingConsentId === consent.id}
                                      title="Elimina consenso"
                                      className="px-2 py-1"
                                    >
                                      {deletingConsentId === consent.id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        <i className="fas fa-trash"></i>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </Card.Header>
                              <Card.Body className="py-2 px-3 bg-white">
                                <div
                                  className="text-muted small mb-2"
                                  style={{ maxHeight: '100px', overflowY: 'auto' }}
                                  dangerouslySetInnerHTML={{ __html: consent.description }}
                                />
                                {consent.checkboxLabel && (
                                  <div className="small text-secondary">
                                    <i className="fas fa-check-square me-1"></i>
                                    <strong>Testo Checkbox:</strong> {consent.checkboxLabel}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          ))}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Gestione Ruoli */}
                  <Accordion.Item eventKey="roles">
                    <Accordion.Header>
                      <i className="fas fa-user-tag me-2"></i>
                      Gestione Ruoli
                    </Accordion.Header>
                    <Accordion.Body>
                      <RoleManagement />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Storico Invii Email */}
                  <Accordion.Item eventKey="reminder-logs">
                    <Accordion.Header>
                      <i className="fas fa-history me-2"></i>
                      Storico Invii Email
                    </Accordion.Header>
                    <Accordion.Body>
                      {logsLoading ? (
                        <div className="text-center">
                          <span className="spinner-border" />
                        </div>
                      ) : reminderLogs.length > 0 ? (
                        <>
                          <div className="table-responsive">
                            <Table striped bordered hover>
                              <thead>
                                <tr>
                                  <th>Data</th>
                                  <th>Atleta</th>
                                  <th>Email</th>
                                  <th>Tipo</th>
                                  <th>Stato</th>
                                  <th>Note</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reminderLogs.map(log => (
                                  <tr key={log.id}>
                                    <td>{moment(log.sentAt).format('DD/MM/YYYY HH:mm')}</td>
                                    <td>{log.user?.firstName} {log.user?.lastName}</td>
                                    <td>{log.user?.email}</td>
                                    <td>
                                      {log.emailType === 'before' && <span className="badge bg-info">Prima</span>}
                                      {log.emailType === 'on' && <span className="badge bg-warning">In Scadenza</span>}
                                      {log.emailType === 'after' && <span className="badge bg-danger">Scaduto</span>}
                                      {log.emailType === 'entries_running_out' && <span className="badge bg-warning text-dark">Ingresso in Esaurimento</span>}
                                      {log.emailType === 'entries_sold_out' && <span className="badge bg-danger">Ingressi Esauriti</span>}
                                    </td>
                                    <td>
                                      {log.status === 'success' ? (
                                        <span className="badge bg-success">Inviato</span>
                                      ) : (
                                        <span className="badge bg-danger">Errore</span>
                                      )}
                                    </td>
                                    <td className="small">{log.errorMessage || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {logsPagination.totalPages > 1 && (
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">
                              <div className="text-muted small">
                                {logsPagination.totalCount > 0 ? (
                                  <>Pagina {logsPagination.currentPage} di {logsPagination.totalPages} ({logsPagination.totalCount} invii totali)</>
                                ) : (
                                  <>Pagina {logsPagination.currentPage} di {logsPagination.totalPages}</>
                                )}
                              </div>
                              <Pagination className="mb-0 flex-wrap justify-content-center">
                                <Pagination.First 
                                  onClick={() => loadReminderLogs(1)}
                                  disabled={logsPagination.currentPage === 1}
                                />
                                <Pagination.Prev 
                                  onClick={() => loadReminderLogs(logsPagination.currentPage - 1)}
                                  disabled={logsPagination.currentPage === 1}
                                />
                                {getPaginationPages(logsPagination.currentPage, logsPagination.totalPages).map((pageItem) => {
                                  if (typeof pageItem === 'string') {
                                    return <Pagination.Ellipsis key={pageItem} disabled />;
                                  }
                                  return (
                                    <Pagination.Item 
                                      key={pageItem}
                                      active={logsPagination.currentPage === pageItem}
                                      onClick={() => loadReminderLogs(pageItem)}
                                    >
                                      {pageItem}
                                    </Pagination.Item>
                                  );
                                })}
                                <Pagination.Next 
                                  onClick={() => loadReminderLogs(logsPagination.currentPage + 1)}
                                  disabled={logsPagination.currentPage === logsPagination.totalPages}
                                />
                                <Pagination.Last 
                                  onClick={() => loadReminderLogs(logsPagination.totalPages)}
                                  disabled={logsPagination.currentPage === logsPagination.totalPages}
                                />
                              </Pagination>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <p>Nessun log di invio email disponibile</p>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Gestione Stanze / Spazi Fisici (Locations) */}
                  <Accordion.Item eventKey="locations">
                    <Accordion.Header>
                      <i className="fas fa-map-marker-alt me-2"></i>
                      Stanze & Spazi Fisici (Location)
                    </Accordion.Header>
                    <Accordion.Body>
                      <LocationSettingsSection />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
          )}

          {/* Email Template Create/Edit Modal */}
          <Modal show={showTemplateModal} onHide={handleCloseTemplateModal} size="lg" backdrop="static">
            <Modal.Header closeButton={!savingTemplate}>
              <Modal.Title>
                <i className="fas fa-file-alt me-2"></i>
                {editingTemplate ? (editingTemplate.isSystem ? 'Modifica Template di Sistema' : 'Modifica Template') : 'Nuovo Template'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nome Template *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Es: Benvenuto, Promemoria pagamento..."
                  value={templateFormData.name}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={savingTemplate || Boolean(editingTemplate?.isSystem)}
                />
                {editingTemplate?.isSystem && (
                  <Form.Text className="text-muted">
                    <i className="fas fa-lock me-1"></i>
                    Il nome dei template di sistema è predefinito e non può essere modificato.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Oggetto Email *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Oggetto dell'email"
                  value={templateFormData.subject}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, subject: e.target.value }))}
                  disabled={savingTemplate}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Corpo Email (HTML)</Form.Label>
                <ReactQuill
                  ref={templateQuillRef}
                  theme="snow"
                  value={templateFormData.body}
                  onChange={(value) => setTemplateFormData(prev => ({ ...prev, body: value }))}
                  modules={templateQuillModules}
                  formats={templateQuillFormats}
                  placeholder="Scrivi il contenuto del template..."
                  readOnly={savingTemplate}
                  style={{ height: '250px', marginBottom: '50px' }}
                />
              </Form.Group>

              <Alert variant="info" className="mt-3">
                <i className="fas fa-tags me-2"></i>
                {getTemplateVariablesHint(editingTemplate ? editingTemplate.name : templateFormData.name)}
                <div className="mt-1">
                  <small className="text-muted">Le variabili verranno sostituite con i dati reali al momento dell'invio.</small>
                </div>
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseTemplateModal} disabled={savingTemplate}>
                Annulla
              </Button>
              <Button variant="primary" onClick={handleSaveTemplate} disabled={savingTemplate}>
                {savingTemplate ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    {editingTemplate ? 'Aggiorna' : 'Crea'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Portal Attachment Create/Edit Modal */}
          <Modal show={showAttachmentModal} onHide={handleCloseAttachmentModal} size="lg" backdrop="static">
            <Modal.Header closeButton={!savingAttachment}>
              <Modal.Title>
                <i className="fas fa-paperclip me-2"></i>
                {editingAttachment ? 'Modifica Allegato Portale' : 'Nuovo Allegato Portale'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Titolo Allegato *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Es: Regolamento Generale, Statuto Sociale, Modulo Privacy..."
                  value={attachmentFormData.title}
                  onChange={(e) => setAttachmentFormData(prev => ({ ...prev, title: e.target.value }))}
                  disabled={savingAttachment}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrizione (Opzionale)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Note o istruzioni aggiuntive per l'atleta relative al documento..."
                  value={attachmentFormData.description}
                  onChange={(e) => setAttachmentFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={savingAttachment}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  File Documento {editingAttachment ? '(Lascia vuoto per mantenere il file attuale)' : '*'}
                </Form.Label>
                <Form.Control
                  type="file"
                  ref={attachmentFileInputRef}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const selected = e.target.files?.[0] || null;
                    setAttachmentFormData(prev => ({ ...prev, file: selected }));
                  }}
                  disabled={savingAttachment}
                />
                <Form.Text className="text-muted">
                  Formati ammessi: PDF, Word (.doc, .docx), Immagini (.jpg, .png, .webp). Dimensione max: 25MB.
                  {editingAttachment?.fileName && (
                    <div className="mt-1">
                      <span className="fw-semibold text-primary">File attuale: </span>
                      {editingAttachment.fileName} ({formatAttachmentSize(editingAttachment.fileSize)})
                    </div>
                  )}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="modal-attachment-enabled"
                  label="Rendi visibile e richiedi accettazione nel portale atleta"
                  checked={attachmentFormData.isEnabled}
                  onChange={(e) => setAttachmentFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                  disabled={savingAttachment}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseAttachmentModal} disabled={savingAttachment}>
                Annulla
              </Button>
              <Button variant="primary" onClick={handleSaveAttachment} disabled={savingAttachment}>
                {savingAttachment ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    {editingAttachment ? 'Aggiorna Allegato' : 'Carica Allegato'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Portal Consent Create/Edit Modal */}
          <Modal show={showConsentModal} onHide={handleCloseConsentModal} size="lg" backdrop="static">
            <Modal.Header closeButton={!savingConsent}>
              <Modal.Title>
                <i className="fas fa-file-contract me-2"></i>
                {editingConsent ? `Modifica Consenso (v${editingConsent.version || 1})` : 'Nuovo Consenso Portale'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editingConsent && (
                <Alert variant="warning" className="small py-2 mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Se modifichi il <strong>titolo</strong> o il <strong>testo descrittivo</strong>, la versione verrà incrementata a <strong>v{(editingConsent.version || 1) + 1}</strong> e gli atleti dovranno rinnovare il consenso nel loro portale.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Titolo Consenso *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Es: Consenso al Trattamento dei Dati Personali (GDPR)..."
                  value={consentFormData.title}
                  onChange={(e) => setConsentFormData(prev => ({ ...prev, title: e.target.value }))}
                  disabled={savingConsent}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tipologia Consenso</Form.Label>
                    <Form.Check
                      type="switch"
                      id="modal-consent-mandatory"
                      label={consentFormData.isMandatory ? 'Obbligatorio (Bloccante per il profilo)' : 'Facoltativo (Revocabile liberamente)'}
                      checked={consentFormData.isMandatory}
                      onChange={(e) => setConsentFormData(prev => ({ ...prev, isMandatory: e.target.checked }))}
                      disabled={savingConsent}
                      className="fw-semibold"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Stato di default</Form.Label>
                    <Form.Check
                      type="switch"
                      id="modal-consent-enabled"
                      label={consentFormData.isEnabled ? 'Attivo e visibile' : 'Disattivato'}
                      checked={consentFormData.isEnabled}
                      onChange={(e) => setConsentFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                      disabled={savingConsent}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Testo Accettazione Checkbox</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Es: Accetto il trattamento, Dichiaro di aver letto..."
                  value={consentFormData.checkboxLabel}
                  onChange={(e) => setConsentFormData(prev => ({ ...prev, checkboxLabel: e.target.value }))}
                  disabled={savingConsent}
                />
                <Form.Text className="text-muted">
                  Testo affiancato alla casella di spunta per l&apos;atleta (se vuoto sarà &quot;Accetto&quot;).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Testo Integrale / Informativa (HTML / Rich Text) *</Form.Label>
                <ReactQuill
                  theme="snow"
                  value={consentFormData.description || ''}
                  onChange={(value) => setConsentFormData(prev => ({ ...prev, description: value }))}
                  modules={templateQuillModules}
                  formats={templateQuillFormats}
                  placeholder="Inserisci il testo completo dell'informativa o del regolamento..."
                  readOnly={savingConsent}
                  style={{ height: '220px', marginBottom: '45px' }}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseConsentModal} disabled={savingConsent}>
                Annulla
              </Button>
              <Button variant="primary" onClick={handleSaveConsent} disabled={savingConsent}>
                {savingConsent ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    {editingConsent ? 'Aggiorna Consenso' : 'Crea Consenso'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default Settings;
